import React from 'react';

export function BakingIssues() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Common Baking Issues</h3>
        <p className="mb-4">
          The final stage of bread making is crucial for achieving the perfect loaf. Understanding common baking issues and their solutions will help you consistently produce bread with great crust, crumb, and overall appearance.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Poor Oven Spring</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Symptoms</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Flat or low-profile loaf</li>
              <li>Dense crumb structure</li>
              <li>Minimal expansion at score lines</li>
              <li>Lack of "ears" on scoring</li>
            </ul>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Common Causes</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Insufficient steam</li>
                <li>Oven not hot enough</li>
                <li>Over-proofed dough</li>
                <li>Poor dough strength</li>
                <li>Inadequate scoring</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg text-amber-400 mb-2">Solutions</h4>
              <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
                <li>Ensure proper steam generation</li>
                <li>Preheat oven thoroughly (45-60 minutes)</li>
                <li>Monitor proofing carefully</li>
                <li>Develop stronger gluten structure</li>
                <li>Score deeper and at proper angle</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Crust Issues</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Pale Crust</h4>
              <div>
                <h5 className="text-amber-400 text-sm">Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Too much steam</li>
                  <li>Low oven temperature</li>
                  <li>Short baking time</li>
                  <li>Poor heat distribution</li>
                </ul>
              </div>
              <div className="mt-3">
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Remove steam after 15-20 minutes</li>
                  <li>Increase oven temperature</li>
                  <li>Extend baking time</li>
                  <li>Use baking stone/steel</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Burnt Crust</h4>
              <div>
                <h5 className="text-amber-400 text-sm">Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Oven too hot</li>
                  <li>Baking too long</li>
                  <li>Rack position too high/low</li>
                  <li>Sugar in dough</li>
                </ul>
              </div>
              <div className="mt-3">
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Reduce oven temperature</li>
                  <li>Adjust baking time</li>
                  <li>Use middle rack position</li>
                  <li>Cover with foil if needed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Bottom Crust Problems</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Burnt Bottom</h4>
              <div>
                <h5 className="text-amber-400 text-sm">Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Direct heat too intense</li>
                  <li>Baking stone too hot</li>
                  <li>Rack position too low</li>
                  <li>Dark baking pan</li>
                </ul>
              </div>
              <div className="mt-3">
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Double up baking sheets</li>
                  <li>Use cornmeal or semolina</li>
                  <li>Raise rack position</li>
                  <li>Use lighter colored pans</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white/5 p-4 rounded-lg">
              <h4 className="text-lg text-amber-400 mb-2">Pale/Soft Bottom</h4>
              <div>
                <h5 className="text-amber-400 text-sm">Causes:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Insufficient bottom heat</li>
                  <li>Stone/steel not preheated</li>
                  <li>Too much insulation</li>
                  <li>Steam trapped underneath</li>
                </ul>
              </div>
              <div className="mt-3">
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Preheat stone/steel longer</li>
                  <li>Lower rack position slightly</li>
                  <li>Remove parchment late in bake</li>
                  <li>Finish baking directly on rack</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Steam-Related Issues</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Insufficient Steam</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Effects:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Poor oven spring</li>
                  <li>Dull crust appearance</li>
                  <li>Thick, tough crust</li>
                  <li>Burst seams</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Use Dutch oven or cloche</li>
                  <li>Improve steam generation</li>
                  <li>Seal oven better</li>
                  <li>Pre-steam oven cavity</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Excess Steam</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-amber-400 text-sm">Effects:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Pale, thick crust</li>
                  <li>Leather-like texture</li>
                  <li>Extended baking time</li>
                  <li>Wrinkled crust</li>
                </ul>
              </div>
              <div>
                <h5 className="text-amber-400 text-sm">Solutions:</h5>
                <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                  <li>Remove steam earlier</li>
                  <li>Reduce steam quantity</li>
                  <li>Vent oven properly</li>
                  <li>Adjust Dutch oven timing</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Baking Environment Optimization</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Oven Setup</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Position stone/steel correctly</li>
              <li>Verify temperature accuracy</li>
              <li>Check for hot spots</li>
              <li>Ensure proper ventilation</li>
              <li>Consider heat distribution</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Temperature Management</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Preheat thoroughly (45-60 minutes)</li>
              <li>Monitor temperature drops</li>
              <li>Adjust for different dough types</li>
              <li>Consider thermal mass</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Steam Control</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Time steam removal correctly</li>
              <li>Use appropriate steam method</li>
              <li>Monitor humidity levels</li>
              <li>Adapt to oven characteristics</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}